import React, { useEffect } from "react";
import "@/App.css";
import "./lib/i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./lib/auth";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import Features from "./pages/Features";
import About from "./pages/About";
import Services from "./pages/Services";
import Community from "./pages/Community";
import Pricing from "./pages/Pricing";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import Account from "./pages/Account";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Support from "./pages/Support";
import Payment from "./pages/Payment";
import Download from "./pages/Download";
import DeleteAccount from "./pages/DeleteAccount";
import DeleteAccountVerify from "./pages/DeleteAccountVerify";
import Affiliate from "./pages/Affiliate";
import Fitness from "./pages/Fitness";
import AppFitness from "./app/AppFitness";
import PassiveClickBeacon from "./components/PassiveClickBeacon";
import PublisherLogin from "./pages/PublisherLogin";
import PublisherDashboard from "./pages/PublisherDashboard";
import AdminPublishers from "./pages/AdminPublishers";
import AdminUsers from "./pages/AdminUsers";
import AppProxyPlaceholder from "./pages/AppProxyPlaceholder";
import { AppAuthProvider } from "./app/AuthContext";
import AppShell, { RequireAuth } from "./app/AppShell";
import { LoginPage, SignupPage } from "./app/AuthPages";
import { ForgotPasswordPage, ResetPasswordPage } from "./app/PasswordPages";
import { AppHome, AppCycle, AppInsights, AppPregnancy, AppAICoach, AppDaughterCare, AppCommunity, AppProfile, AppSettings, AppSubscription, AppCalendar, AppLogs, AppTrends, AppMedications, AppPartner, AppWearables, PremiumLock } from "./app/pages";
import FeatureCycle from "./pages/FeatureCycle";
import AdminConversions from "./pages/AdminConversions";
import AdminPublisherLinks from "./pages/AdminPublisherLinks";
import FallingHeartsCanvas from "./components/FallingHeartsCanvas";
import { FeaturePregnancy, FeatureAICoach, FeatureDaughterCare, FeatureCommunity, FeatureInsights } from "./pages/FeaturePages";
import { initAttribution } from "./lib/attribution";
import { initLanguage, useFcLang } from "./lib/fc3i18n";
import { LangSuggestBar } from "./components/fc3/LanguageUI";
if (typeof window !== "undefined") { initLanguage(); }
import { applyRouteMeta } from "./lib/seo";

function ScrollToTop() {
    const { pathname, search } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    // Capture UTM/click-id/publisher attribution on EVERY navigation. Idempotent + first-touch immutable.
    useEffect(() => { initAttribution(); }, [pathname, search]);
    useEffect(() => { applyRouteMeta(pathname); }, [pathname]);
    return null;
}

function Layout({ children }) {
  const fcKey = useFcLang();
    const { pathname } = useLocation();
    const bare = pathname.startsWith("/admin") || pathname.startsWith("/payment") || pathname.startsWith("/app") || pathname.startsWith("/publisher");
    const isHome = pathname === "/";
    return (
        <>
            {!bare && <Header key={"h" + fcKey} />}
            {/* Falling-hearts flourish is a marketing decoration for the public
             * homepage ONLY. Every internal surface (auth, admin, in-app,
             * publisher panel, payment, pricing, about, etc.) renders clean
             * without the overlay so power users and partners aren't
             * distracted by ambient motion. */}
            {/* FemCare 3.0: falling hearts removed */}
            <main className={`relative z-10 ${!bare && !isHome ? "pt-[74px]" : ""}`}><React.Fragment key={fcKey}>{children}</React.Fragment></main>
            {!bare && <Footer key={"f" + fcKey} />}
      <LangSuggestBar key={"s" + fcKey} />
        </>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <ScrollToTop />
                    <PassiveClickBeacon />
                    <Layout>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/admin/conversions" element={<AdminConversions />} />
                            <Route path="/admin/publisher-links" element={<AdminPublisherLinks />} />
                            <Route path="/admin/publishers" element={<AdminPublishers />} />
                            <Route path="/admin/users" element={<AdminUsers />} />
                            <Route path="/publisher/login" element={<PublisherLogin />} />
                            <Route path="/publisher" element={<PublisherDashboard />} />
                            <Route path="/features" element={<Features />} />
                            <Route path="/features/cycle" element={<FeatureCycle />} />
                            <Route path="/features/pregnancy" element={<FeaturePregnancy />} />
                            <Route path="/features/ai-coach" element={<FeatureAICoach />} />
                            <Route path="/features/daughter-care" element={<FeatureDaughterCare />} />
                            <Route path="/features/community" element={<FeatureCommunity />} />
                            <Route path="/features/insights" element={<FeatureInsights />} />
                            <Route path="/fitness" element={<Fitness />} />
          <Route path="/about" element={<About />} />
                            <Route path="/services" element={<Services />} />
                            <Route path="/community" element={<Community />} />
                            <Route path="/pricing" element={<Pricing />} />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/blog" element={<Blog />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route path="/account" element={<Account />} />
                            <Route path="/privacy" element={<Privacy />} />
                            <Route path="/terms" element={<Terms />} />
                            <Route path="/support" element={<Support />} />
                            <Route path="/payment" element={<Payment />} />
                            <Route path="/download" element={<Download />} />
                            <Route path="/delete-account" element={<DeleteAccount />} />
                            <Route path="/delete-account/verify" element={<DeleteAccountVerify />} />
                            <Route path="/affiliate" element={<Affiliate />} />
                            <Route path="/app/login" element={<AppAuthProvider><LoginPage /></AppAuthProvider>} />
                            <Route path="/app/signup" element={<AppAuthProvider><SignupPage /></AppAuthProvider>} />
                            <Route path="/app/forgot-password" element={<ForgotPasswordPage />} />
                            <Route path="/app/reset-password" element={<ResetPasswordPage />} />
                            <Route path="/app" element={<AppAuthProvider><RequireAuth><AppShell /></RequireAuth></AppAuthProvider>}>
                                <Route index element={<AppHome />} />
                                <Route path="home" element={<AppHome />} />
                                <Route path="cycle" element={<AppCycle />} />
                                <Route path="calendar" element={<AppCalendar />} />
                                <Route path="logs" element={<AppLogs />} />
                                <Route path="insights" element={<PremiumLock feature="Insights"><AppInsights /></PremiumLock>} />
                                <Route path="trends" element={<PremiumLock feature="Trends"><AppTrends /></PremiumLock>} />
                                <Route path="pregnancy" element={<PremiumLock feature="Pregnancy"><AppPregnancy /></PremiumLock>} />
                                <Route path="ai-coach" element={<PremiumLock feature="AI Coach"><AppAICoach /></PremiumLock>} />
                                <Route path="daughter-care" element={<AppDaughterCare />} />
                                <Route path="community" element={<AppCommunity />} />
                                <Route path="medications" element={<PremiumLock feature="Medications"><AppMedications /></PremiumLock>} />
                                <Route path="partner" element={<PremiumLock feature="Partner"><AppPartner /></PremiumLock>} />
                                <Route path="wearables" element={<PremiumLock feature="Wearables"><AppWearables /></PremiumLock>} />
            <Route path="fitness" element={<AppFitness />} />
                                <Route path="profile" element={<AppProfile />} />
                                <Route path="settings" element={<AppSettings />} />
                                <Route path="subscription" element={<AppSubscription />} />
                            </Route>
                            <Route path="/app/*" element={<AppProxyPlaceholder />} />
                        </Routes>
                    </Layout>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
